import { FRAMES, type SPACING } from "odyssey-shared";
import { cutToFit } from "../../../ui/kit/fit.ts";
import { pixelTextWidth } from "../../../ui/kit/pixel-text.ts";
import { ABOVE_HEAD, LINE } from "../nameplates/layout.ts";
import { bubbleGlyphs } from "./glyphs.ts";

/**
 * What a chat bubble shows of a line and where it stands (P1.9-T23, ADR-068 decision 9 and its amendment of
 * 2026-09-27). The line is drawn in the pixel face's glyphs (`glyphs.ts`) from the plain bitmap font, which has no
 * outline, wrapped within `BUBBLE_WRAP` logical px of the face's advances at 1x, the widths the web font shares
 * (`pixelTextWidth`), and cut after `BUBBLE_LINES` lines with U+2026 as the message line cuts a reply
 * (`cutToFit`). The frame is the atlas's `ui/frame/bubble` on its token insets, holding the text with
 * `BUBBLE_PADDING` of fill on every side; the tail (`TAIL`) hangs centred under it, and its point stands
 * `BUBBLE_GAP` above the speaker's nameplate, the frame centred across on the speaker's feet. As for the
 * nameplates, every position is computed in device pixels from an anchor that is itself a whole device pixel and
 * then divided by the UI scale, so the frame, the tail and the text stand on the device grid at every UI scale and
 * never judder where the UI scale and the world zoom differ.
 */

/**
 * How wide a bubble's line runs at most: logical pixels of the pixel face's advances at 1x.
 */
export const BUBBLE_WRAP = 120;

/**
 * The most lines a bubble shows. A longer line is cut on the last with U+2026; the chat log holds it whole.
 */
export const BUBBLE_LINES = 3;

/**
 * Logical pixels of the frame's fill between its insets and the text's box on every side, a spacing step.
 */
export const BUBBLE_PADDING = 2 satisfies (typeof SPACING)[number];

/**
 * Logical pixels between the tail's point and the top of its speaker's nameplate, a spacing step.
 */
export const BUBBLE_GAP = 2 satisfies (typeof SPACING)[number];

/**
 * Logical pixels between two lines of a bubble beyond the face's line height: none. The plain font draws every
 * glyph's ink inside its line and has no outline, so no line's glyphs reach another's.
 */
export const BUBBLE_LINE_GAP = 0;

/**
 * The tail under a bubble, the atlas's `ui/chat/bubble-tail_5`: a downward triangle 5 wide and 3 tall in the
 * frame's edge colour, whose top row lies over the frame's bottom edge row, so two rows show below the frame.
 */
export const TAIL = Object.freeze({ width: 5, height: 3, overlap: 1 });

/**
 * How far the tail shows below the frame, in logical pixels.
 */
const TAIL_BELOW = TAIL.height - TAIL.overlap;

/**
 * The bubble frame's insets on each side, from the token table.
 */
const INSETS = FRAMES.bubble.insets;

/**
 * A bubble frame's size in logical pixels.
 */
export interface BubbleSize {
  readonly width: number;
  readonly height: number;
}

/**
 * A bubble's parts, written in place so a bubble placed every frame allocates nothing: the frame's, the text's and
 * the tail's top left in logical pixels, the frame's size in logical pixels and its box in device pixels.
 */
export interface BubbleLayout {
  frameX: number;
  frameY: number;
  textX: number;
  textY: number;
  tailX: number;
  tailY: number;
  width: number;
  height: number;
  deviceLeft: number;
  deviceTop: number;
  deviceWidth: number;
  deviceHeight: number;
}

/**
 * Wraps a text in the pixel face's glyphs into lines no wider than `BUBBLE_WRAP`: each line breaks at the last
 * space that lets it fit and drops the spaces it breaks at, a word that does not fit moves to the next line, and a
 * word wider than a line breaks inside itself. Spaces inside a line stay as they were typed.
 *
 * @param text The text, in the face's glyphs (`bubbleGlyphs`).
 * @returns Its lines, none for a text of nothing but spaces.
 * @throws RangeError when the text holds a character the face has no glyph for.
 * @example
 * wrapBubbleText(`${"M".repeat(10)} ${"M".repeat(10)}`);
 * // ["MMMMMMMMMM", "MMMMMMMMMM"]
 */
export function wrapBubbleText(text: string): string[] {
  const points = Array.from(text);
  const lines: string[] = [];
  let start = 0;
  while (start < points.length) {
    while (points[start] === " ") {
      start += 1;
    }
    if (start >= points.length) {
      break;
    }
    let end = start;
    let width = 0;
    let space = -1;
    while (end < points.length) {
      const point = points[end] as string;
      if (point === " ") {
        space = end;
      }
      const advance = pixelTextWidth(point);
      if (width + advance > BUBBLE_WRAP) {
        break;
      }
      width += advance;
      end += 1;
    }
    let stop = Math.max(end, start + 1);
    let next = stop;
    if (end < points.length && space > start) {
      stop = space;
      next = space + 1;
    }
    lines.push(points.slice(start, stop).join("").trimEnd());
    start = next;
  }
  return lines;
}

/**
 * The lines a bubble shows of a Local line: its text in composed form and in the pixel face's glyphs
 * (`bubbleGlyphs`), wrapped within `BUBBLE_WRAP`, and when they run past `BUBBLE_LINES`, the longest start of the
 * text that wraps within them with U+2026 after it, any space before the cut dropped.
 *
 * @param text The line's text as the chat rule leaves it.
 * @returns At most `BUBBLE_LINES` lines, none when the text draws nothing but spaces.
 * @example
 * bubbleLines("Well met.");
 * // ["Well met."]
 */
export function bubbleLines(text: string): string[] {
  const fitted = cutToFit(bubbleGlyphs(text), (candidate) => wrapBubbleText(candidate).length <= BUBBLE_LINES);
  return wrapBubbleText(fitted);
}

/**
 * The frame that holds a bubble's lines: the widest line's advances across, every line of the pixel face's line
 * height down with `BUBBLE_LINE_GAP` between two lines, inside the padding and the frame's insets.
 *
 * @param lines The bubble's lines, in the face's glyphs.
 * @returns The frame's size in logical pixels.
 * @throws RangeError when a line holds a character the face has no glyph for.
 */
export function bubbleSize(lines: readonly string[]): BubbleSize {
  let widest = 0;
  for (const line of lines) {
    widest = Math.max(widest, pixelTextWidth(line));
  }
  return {
    width: INSETS.left + BUBBLE_PADDING + widest + BUBBLE_PADDING + INSETS.right,
    height: INSETS.top + BUBBLE_PADDING + lines.length * LINE.height + Math.max(0, lines.length - 1) * BUBBLE_LINE_GAP + BUBBLE_PADDING + INSETS.bottom,
  };
}

/**
 * A layout to write into.
 *
 * @returns A layout of zeros.
 */
export function newBubbleLayout(): BubbleLayout {
  return { frameX: 0, frameY: 0, textX: 0, textY: 0, tailX: 0, tailY: 0, width: 0, height: 0, deviceLeft: 0, deviceTop: 0, deviceWidth: 0, deviceHeight: 0 };
}

/**
 * Places a bubble over its speaker: centred across on the feet, the tail's point `BUBBLE_GAP` above the top of the
 * nameplate, which stands `ABOVE_HEAD` and a line of the nameplate role above the head, and the tail centred under
 * the frame on the logical grid, a pixel to the left where the two widths differ by an odd count.
 *
 * @param anchorX The device pixel the bubble is centred on, the speaker's feet across.
 * @param headY The device pixel of the speaker's head, which the nameplate and the bubble stand above.
 * @param scale The UI scale.
 * @param width The frame's width in logical pixels.
 * @param height The frame's height in logical pixels.
 * @param out Where to write the layout.
 * @returns `out`.
 */
export function bubbleLayout(anchorX: number, headY: number, scale: number, width: number, height: number, out: BubbleLayout): BubbleLayout {
  const left = anchorX - Math.floor((width * scale) / 2);
  const top = headY - (ABOVE_HEAD + LINE.height + BUBBLE_GAP + TAIL_BELOW + height) * scale;
  out.width = width;
  out.height = height;
  out.frameX = left / scale;
  out.frameY = top / scale;
  out.textX = out.frameX + INSETS.left + BUBBLE_PADDING;
  out.textY = out.frameY + INSETS.top + BUBBLE_PADDING;
  out.tailX = out.frameX + Math.floor((width - TAIL.width) / 2);
  out.tailY = out.frameY + height - TAIL.overlap;
  out.deviceLeft = left;
  out.deviceTop = top;
  out.deviceWidth = width * scale;
  out.deviceHeight = height * scale;
  return out;
}
